<template>
  <div>
    <Head :title="`Caja: ${cashRegister.name}`" />

    <!-- Header -->
    <div class="flex justify-between items-center mb-8">
      <div class="flex items-center gap-4">
        <button
          class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="text-3xl font-bold">{{ cashRegister.name }}</h1>
          <p class="text-gray-600 mt-1">{{ cashRegister.description }}</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-3">
        <Link
          v-if="cashRegister.status === 'closed'"
          :href="`/cash-registers/${cashRegister.id}/edit`"
          class="btn-blue"
        >
          <i class="fa fa-edit mr-2"></i>
          Editar
        </Link>

        <button
          v-if="cashRegister.status === 'closed'"
          @click="openCashRegister"
          class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
        >
          <i class="fa fa-play mr-2"></i>
          Abrir Caja
        </button>

        <button
          v-if="cashRegister.status === 'open'"
          @click="showWithdrawalModal = true"
          class="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors flex items-center cursor-pointer"
          style="background-color: #d97706;"
        >
          <i class="fa fa-hand-holding-usd mr-2"></i>
          Retiro de Efectivo
        </button>

        <button
          v-if="cashRegister.status === 'open'"
          @click="closeCashRegister"
          class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
        >
          <i class="fa fa-stop mr-2"></i>
          Cerrar Caja
        </button>
      </div>
    </div>

    <!-- Status Card -->
    <div class="bg-white rounded-lg shadow-sm mb-6">
      <div class="flex items-center px-6 py-4 bg-teal-800 text-white rounded-t-lg">
        <h2 class="text-lg font-semibold">Estado de la Caja</h2>
      </div>

      <div class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Status -->
          <div>
            <h3 class="text-sm font-medium text-gray-500">Estado</h3>
            <span
              :class="getStatusClass(cashRegister.status)"
              class="px-3 py-1 rounded-full text-sm font-medium mt-1 inline-block"
            >
              {{ getStatusText(cashRegister.status) }}
            </span>
          </div>

          <!-- Opening Balance -->
          <div>
            <h3 class="text-sm font-medium text-gray-500">Saldo Inicial</h3>
            <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(cashRegister.opening_balance) }}</p>
            <p class="text-sm text-gray-500">{{ formatCurrency(cashRegister.opening_balance_bs, 'VES') }}</p>
            <p class="text-sm text-gray-500">{{ formatCurrency(cashRegister.opening_balance_cop, 'COP') }}</p>

            <!-- Comparativa con Cierre Anterior -->
            <div v-if="summary && summary.opening_comparison" class="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div class="text-[11px] font-bold uppercase tracking-wider text-gray-700 flex items-center justify-between mb-1.5">
                <span><i class="fa fa-history mr-1 text-teal-600"></i> Vs. Cierre Anterior</span>
                <span class="text-[10px] text-gray-500 font-normal">({{ summary.opening_comparison.previous_closure_date }})</span>
              </div>
              <div class="space-y-1.5 text-xs">
                <!-- COP -->
                <div class="flex items-center justify-between border-b border-gray-100 pb-1">
                  <span class="text-gray-600">COP (Dejó {{ formatCurrency(summary.opening_comparison.previous_closing.cop, 'COP') }}):</span>
                  <span v-if="summary.opening_comparison.diff.cop === 0" class="font-bold text-emerald-600 flex items-center gap-1">
                    <i class="fa fa-check-circle"></i> Cuadrado (0)
                  </span>
                  <span v-else-if="summary.opening_comparison.diff.cop > 0" class="font-bold text-blue-600">
                    +{{ formatCurrency(summary.opening_comparison.diff.cop, 'COP') }} (Sobrante)
                  </span>
                  <span v-else class="font-bold text-red-600">
                    {{ formatCurrency(summary.opening_comparison.diff.cop, 'COP') }} (Faltante)
                  </span>
                </div>
                <!-- USD -->
                <div class="flex items-center justify-between border-b border-gray-100 pb-1">
                  <span class="text-gray-600">USD (Dejó {{ formatCurrency(summary.opening_comparison.previous_closing.usd) }}):</span>
                  <span v-if="Math.abs(summary.opening_comparison.diff.usd) < 0.01" class="font-bold text-emerald-600 flex items-center gap-1">
                    <i class="fa fa-check-circle"></i> Cuadrado ($0)
                  </span>
                  <span v-else-if="summary.opening_comparison.diff.usd > 0" class="font-bold text-blue-600">
                    +{{ formatCurrency(summary.opening_comparison.diff.usd) }} (Sobrante)
                  </span>
                  <span v-else class="font-bold text-red-600">
                    {{ formatCurrency(summary.opening_comparison.diff.usd) }} (Faltante)
                  </span>
                </div>
                <!-- VES -->
                <div class="flex items-center justify-between">
                  <span class="text-gray-600">Bs (Dejó {{ formatCurrency(summary.opening_comparison.previous_closing.bs, 'VES') }}):</span>
                  <span v-if="Math.abs(summary.opening_comparison.diff.bs) < 0.01" class="font-bold text-emerald-600 flex items-center gap-1">
                    <i class="fa fa-check-circle"></i> Cuadrado (Bs. 0)
                  </span>
                  <span v-else-if="summary.opening_comparison.diff.bs > 0" class="font-bold text-blue-600">
                    +{{ formatCurrency(summary.opening_comparison.diff.bs, 'VES') }} (Sobrante)
                  </span>
                  <span v-else class="font-bold text-red-600">
                    {{ formatCurrency(summary.opening_comparison.diff.bs, 'VES') }} (Faltante)
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Current Balance -->
          <div>
            <h3 class="text-sm font-medium text-gray-500">Saldo Actual</h3>
            <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(cashRegister.current_balance) }}</p>
            <p class="text-sm text-gray-500">{{ formatCurrency(cashRegister.current_balance_bs, 'VES') }}</p>
            <p class="text-sm text-gray-500">{{ formatCurrency(cashRegister.current_balance_cop, 'COP') }}</p>
          </div>
        </div>

        <!-- Opened/Closed Info -->
        <div class="mt-6 pt-6 border-t border-gray-200">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 class="text-sm font-medium text-gray-500">Abierta por</h3>
              <p class="text-sm text-gray-900">{{ cashRegister.opened_by?.name || 'N/A' }}</p>
              <p class="text-xs text-gray-500">{{ cashRegister.opened_at ? formatDate(cashRegister.opened_at) : 'N/A' }}</p>
            </div>

            <div v-if="cashRegister.closed_at">
              <h3 class="text-sm font-medium text-gray-500">Cerrada por</h3>
              <p class="text-sm text-gray-900">{{ cashRegister.closed_by?.name || 'N/A' }}</p>
              <p class="text-xs text-gray-500">{{ formatDate(cashRegister.closed_at) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sumatoria de la Caja (Sesión Actual) -->
    <div v-if="summary" class="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
      <div class="flex items-center justify-between px-6 py-4 bg-gray-800 text-white">
        <div class="flex items-center gap-2">
          <i class="fa fa-calculator text-teal-400"></i>
          <h2 class="text-lg font-semibold">Sumatoria de Caja (Sesión Actual)</h2>
        </div>
        <span class="text-xs bg-gray-700 px-3 py-1 rounded-full text-gray-200">
          {{ summary.sales?.count || 0 }} venta{{ (summary.sales?.count === 1) ? '' : 's' }} registrada{{ (summary.sales?.count === 1) ? '' : 's' }}
        </span>
      </div>

      <div class="p-6">
        <!-- Tarjetas principales de totales -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <!-- Total Ventas Realizadas -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-semibold text-blue-700 uppercase tracking-wider">Total Ventas Facturadas</span>
              <i class="fa fa-shopping-bag text-blue-500"></i>
            </div>
            <p class="text-2xl font-black text-blue-900">{{ formatCurrency(summary.sales?.total_usd, 'USD') }}</p>
            <div class="mt-2 text-xs text-blue-700 space-y-0.5 border-t border-blue-200 pt-1">
              <div><span class="font-medium">Bs:</span> {{ formatCurrency(summary.sales?.total_bs, 'VES') }}</div>
              <div><span class="font-medium">COP:</span> {{ formatCurrency(summary.sales?.total_cop, 'COP') }}</div>
            </div>
          </div>

          <!-- Total Efectivo Cobrado en Ventas -->
          <div class="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Cobrado en Efectivo</span>
              <i class="fa fa-money-bill-wave text-emerald-500"></i>
            </div>
            <p class="text-2xl font-black text-emerald-900">{{ formatCurrency(summary.cash_sales?.usd, 'USD') }}</p>
            <div class="mt-2 text-xs text-emerald-700 space-y-0.5 border-t border-emerald-200 pt-1">
              <div><span class="font-medium">Bs:</span> {{ formatCurrency(summary.cash_sales?.bs, 'VES') }}</div>
              <div><span class="font-medium">COP:</span> {{ formatCurrency(summary.cash_sales?.cop, 'COP') }}</div>
            </div>
          </div>

          <!-- Movimientos Extras (Retiros / Ingresos / Avances) -->
          <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-semibold text-amber-700 uppercase tracking-wider">Movimientos / Retiros</span>
              <i class="fa fa-exchange-alt text-amber-500"></i>
            </div>
            <div class="text-xs text-amber-900 space-y-1">
              <div class="flex justify-between">
                <span>Retiros USD:</span>
                <span class="font-bold text-red-600">-{{ formatCurrency(summary.movements?.withdrawals_usd, 'USD') }}</span>
              </div>
              <div class="flex justify-between">
                <span>Retiros Bs:</span>
                <span class="font-bold text-red-600">-{{ formatCurrency(summary.movements?.withdrawals_bs, 'VES') }}</span>
              </div>
              <div class="flex justify-between">
                <span>Retiros COP:</span>
                <span class="font-bold text-red-600">-{{ formatCurrency(summary.movements?.withdrawals_cop, 'COP') }}</span>
              </div>
              <div class="flex justify-between pt-1 border-t border-amber-200">
                <span>Ingresos extras:</span>
                <span class="font-bold text-green-700">+{{ formatCurrency(summary.movements?.deposits_usd, 'USD') }}</span>
              </div>
            </div>
          </div>

          <!-- Saldo Esperado en Gaveta (Físico) -->
          <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-semibold text-purple-700 uppercase tracking-wider">Saldo Esperado en Gaveta</span>
              <i class="fa fa-cash-register text-purple-500"></i>
            </div>
            <p class="text-2xl font-black text-purple-900">{{ formatCurrency(summary.expected_cash?.usd, 'USD') }}</p>
            <div class="mt-2 text-xs text-purple-700 space-y-0.5 border-t border-purple-200 pt-1">
              <div><span class="font-medium">Bs:</span> {{ formatCurrency(summary.expected_cash?.bs, 'VES') }}</div>
              <div><span class="font-medium">COP:</span> {{ formatCurrency(summary.expected_cash?.cop, 'COP') }}</div>
            </div>
          </div>
        </div>

        <!-- Desglose por método de pago -->
        <div class="mt-4">
          <h3 class="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
            <i class="fa fa-list-alt text-teal-600"></i>
            Desglose de Recaudación por Forma de Pago
          </h3>

          <div class="overflow-x-auto border border-gray-200 rounded-lg">
            <table class="w-full text-sm">
              <thead class="bg-gray-50 text-gray-600 font-semibold text-xs uppercase border-b border-gray-200">
                <tr>
                  <th class="py-2.5 px-4 text-left">Método de Pago</th>
                  <th class="py-2.5 px-4 text-center">N° Operaciones</th>
                  <th class="py-2.5 px-4 text-right">Monto USD ($)</th>
                  <th class="py-2.5 px-4 text-right">Monto Bs.</th>
                  <th class="py-2.5 px-4 text-right">Monto COP ($ COP)</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="item in summary.payment_methods" :key="item.method" class="hover:bg-gray-50">
                  <td class="py-2.5 px-4 font-medium text-gray-800 flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-teal-500 inline-block"></span>
                    {{ item.method }}
                  </td>
                  <td class="py-2.5 px-4 text-center font-semibold text-gray-600">{{ item.count }}</td>
                  <td class="py-2.5 px-4 text-right font-medium text-gray-900">{{ formatCurrency(item.usd, 'USD') }}</td>
                  <td class="py-2.5 px-4 text-right font-medium text-gray-700">{{ formatCurrency(item.bs, 'VES') }}</td>
                  <td class="py-2.5 px-4 text-right font-medium text-teal-700">{{ formatCurrency(item.cop, 'COP') }}</td>
                </tr>
                <tr v-if="!summary.payment_methods || summary.payment_methods.length === 0">
                  <td colspan="5" class="py-4 text-center text-gray-500">
                    No se han registrado pagos en esta sesión todavía.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="bg-white rounded-lg shadow-sm">
      <div class="border-b border-gray-200">
        <nav class="flex">
          <button
            @click="activeTab = 'transactions'"
            :class="activeTab === 'transactions' ? 'border-teal-500 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            class="whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm"
          >
            Transacciones
          </button>
          <button
            @click="activeTab = 'orders'"
            :class="activeTab === 'orders' ? 'border-teal-500 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            class="whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2"
          >
            <i class="fa fa-shopping-cart"></i>
            <span>Ventas de la Sesión</span>
            <span v-if="summary && summary.sales" class="bg-teal-100 text-teal-800 text-xs px-2 py-0.5 rounded-full font-bold">
              {{ summary.sales.count }}
            </span>
          </button>
          <button
            @click="activeTab = 'closures'"
            :class="activeTab === 'closures' ? 'border-teal-500 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            class="whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm"
          >
            Cierres
          </button>
        </nav>
      </div>

      <!-- Transactions Tab -->
      <div v-if="activeTab === 'transactions'" class="p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-gray-900">Transacciones</h3>
          <button
            v-if="cashRegister.status === 'open'"
            @click="showTransactionModal = true"
            class="btn-blue"
          >
            <i class="fa fa-plus mr-2"></i>
            Nueva Transacción
          </button>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full whitespace-nowrap">
            <thead>
              <tr class="text-center bg-gray-50">
                <th class="p-3 font-semibold">Tipo</th>
                <th class="p-3 font-semibold">Monto</th>
                <th class="p-3 font-semibold">Descripción</th>
                <th class="p-3 font-semibold">Responsable</th>
                <th class="p-3 font-semibold">Fecha</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="transaction in transactions.data"
                :key="transaction.id"
                class="border-t border-gray-100 hover:bg-gray-50 transition-colors text-center"
              >
                <td class="p-3">
                  <span
                    :class="getTransactionTypeClass(transaction.type)"
                    class="px-2 py-1 rounded-full text-xs font-medium"
                  >
                    {{ getTransactionTypeText(transaction.type) }}
                  </span>
                </td>
                <td class="p-3">
                  <div class="text-right">
                    <p class="font-medium">{{ formatCurrency(transaction.amount) }}</p>
                    <p class="text-xs text-gray-500">{{ formatCurrency(transaction.amount_bs, 'VES') }}</p>
                    <p class="text-xs text-gray-500">{{ formatCurrency(transaction.amount_cop, 'COP') }}</p>
                  </div>
                </td>
                <td class="p-3 text-gray-600">{{ transaction.description }}</td>
                <td class="p-3 text-gray-600">{{ transaction.responsible?.name }}</td>
                <td class="p-3 text-gray-600">{{ formatDate(transaction.created_at) }}</td>
              </tr>
              <tr v-if="!transactions.data || transactions.data.length === 0">
                <td class="px-6 py-4 border-t" colspan="5">No hay transacciones.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <pagination
          v-if="transactions?.meta?.links"
          :links="transactions.meta.links"
          class="mt-6"
        />
      </div>

      <!-- Orders Tab -->
      <div v-if="activeTab === 'orders'" class="p-6">
        <div class="flex justify-between items-center mb-4">
          <div>
            <h3 class="text-lg font-medium text-gray-900">Ventas Realizadas en esta Sesión</h3>
            <p class="text-xs text-gray-500">Listado de ventas asociadas al cajero y tiempo activo de esta caja</p>
          </div>
          <span class="text-sm font-semibold text-gray-700 bg-gray-100 px-3 py-1 rounded-full" v-if="summary?.orders">
            Total: {{ summary.orders.length }} ventas
          </span>
        </div>

        <div class="overflow-x-auto border border-gray-200 rounded-lg">
          <table class="w-full whitespace-nowrap text-sm">
            <thead>
              <tr class="text-center bg-gray-50 border-b border-gray-200">
                <th class="p-3 font-semibold text-left">N° Pedido</th>
                <th class="p-3 font-semibold text-left">Cliente</th>
                <th class="p-3 font-semibold text-left">Vendedor</th>
                <th class="p-3 font-semibold text-right">Total USD</th>
                <th class="p-3 font-semibold text-right">Total Bs</th>
                <th class="p-3 font-semibold text-right">Total COP</th>
                <th class="p-3 font-semibold text-left">Métodos y Pagos</th>
                <th class="p-3 font-semibold text-center">Fecha / Hora</th>
                <th class="p-3 font-semibold text-center">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="order in summary?.orders || []"
                :key="order.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td class="p-3 text-left font-bold text-teal-700">#{{ order.number_order }}</td>
                <td class="p-3 text-left font-medium text-gray-800">{{ order.client_name }}</td>
                <td class="p-3 text-left text-gray-600">{{ order.responsible_name }}</td>
                <td class="p-3 text-right font-black text-gray-900">{{ formatCurrency(order.total, 'USD') }}</td>
                <td class="p-3 text-right text-gray-700">{{ formatCurrency(order.total_bs, 'VES') }}</td>
                <td class="p-3 text-right text-teal-700 font-bold">{{ formatCurrency(order.total_cop, 'COP') }}</td>
                <td class="p-3 text-left">
                  <div class="flex flex-wrap gap-1 max-w-xs">
                    <span
                      v-for="(pay, idx) in order.pays"
                      :key="idx"
                      class="text-xs bg-gray-100 px-2 py-0.5 rounded border border-gray-200 text-gray-700"
                    >
                      <strong class="font-semibold">{{ pay.method }}:</strong>
                      <span v-if="pay.amount_cop > 0"> {{ formatCurrency(pay.amount_cop, 'COP') }}</span>
                      <span v-else-if="pay.amount_bs > 0"> {{ formatCurrency(pay.amount_bs, 'VES') }}</span>
                      <span v-else> {{ formatCurrency(pay.amount, 'USD') }}</span>
                      <span v-if="pay.reference" class="text-gray-500 font-mono text-[10px]"> (ref: {{ pay.reference }})</span>
                    </span>
                  </div>
                </td>
                <td class="p-3 text-center text-xs text-gray-500">{{ order.created_at }}</td>
                <td class="p-3 text-center">
                  <div class="flex items-center justify-center gap-1">
                    <Link
                      :href="`/orders/${order.id}`"
                      class="px-2.5 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition-colors inline-flex items-center"
                      title="Ver Detalle"
                    >
                      <i class="fa fa-eye mr-1"></i> Ver
                    </Link>
                    <a
                      :href="`/orders/${order.id}/report-storer`"
                      target="_blank"
                      class="px-2.5 py-1 text-xs bg-teal-600 hover:bg-teal-700 text-white rounded font-medium transition-colors inline-flex items-center"
                      title="Nota de Entrega PDF"
                    >
                      <i class="fa fa-file-pdf mr-1"></i> PDF
                    </a>
                  </div>
                </td>
              </tr>
              <tr v-if="!summary?.orders || summary.orders.length === 0">
                <td class="px-6 py-8 text-center text-gray-500" colspan="9">
                  <div class="flex flex-col items-center justify-center">
                    <i class="fa fa-shopping-cart text-3xl text-gray-300 mb-2"></i>
                    <p>No hay ventas registradas en esta sesión de caja.</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Closures Tab -->
      <div v-if="activeTab === 'closures'" class="p-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Historial de Cierres</h3>

        <div class="space-y-4">
          <div
            v-for="closure in closures"
            :key="closure.id"
            class="border border-gray-200 rounded-lg p-4"
          >
            <div class="flex justify-between items-start mb-4">
              <div>
                <h4 class="font-medium text-gray-900">Cierre del {{ formatDate(closure.closure_date) }}</h4>
                <p class="text-sm text-gray-500">Cerrado por {{ closure.closed_by?.name }}</p>
              </div>
              <a
                :href="`/cash-closures/${closure.id}/report`"
                class="text-blue-600 hover:text-blue-800 text-sm font-semibold flex items-center"
                target="_blank"
              >
                <i class="fa fa-file-pdf mr-1"></i>
                Ver Reporte PDF
              </a>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span class="text-gray-500">Saldo Inicial:</span>
                <span class="font-medium ml-2">{{ formatCurrency(closure.opening_balance) }}</span>
              </div>
              <div>
                <span class="text-gray-500">Saldo Esperado:</span>
                <span class="font-medium ml-2">{{ formatCurrency(closure.expected_balance) }}</span>
              </div>
              <div>
                <span class="text-gray-500">Saldo Real:</span>
                <span class="font-medium ml-2">{{ formatCurrency(closure.closing_balance) }}</span>
              </div>
              <div>
                <span class="text-gray-500">Diferencia:</span>
                <span
                  :class="closure.difference >= 0 ? 'text-green-600' : 'text-red-600'"
                  class="font-medium ml-2"
                >
                  {{ formatCurrency(closure.difference) }}
                </span>
              </div>
            </div>

            <div v-if="closure.notes" class="mt-4 pt-4 border-t border-gray-200">
              <p class="text-sm text-gray-600">
                <span class="font-medium">Notas:</span> {{ closure.notes }}
              </p>
            </div>
          </div>

          <div v-if="!closures || closures.length === 0" class="text-center py-8 text-gray-500">
            No hay cierres registrados.
          </div>
        </div>
      </div>
    </div>

    <!-- Transaction Modal -->
    <div
      v-if="showTransactionModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="showTransactionModal = false"
    >
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Nueva Transacción</h3>

          <form @submit.prevent="createTransaction">
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Tipo</label>
                <select
                  v-model="transactionForm.type"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                >
                  <option value="withdrawal">Retiro</option>
                  <option value="deposit">Depósito</option>
                  <option value="transfer_out">Transferencia</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700">Monto (USD)</label>
                <input
                  v-model="transactionForm.amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  required
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700">Descripción</label>
                <textarea
                  v-model="transactionForm.description"
                  rows="3"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  required
                ></textarea>
              </div>

              <div v-if="transactionForm.type === 'transfer_out'">
                <label class="block text-sm font-medium text-gray-700">Caja Destino</label>
                <select
                  v-model="transactionForm.cash_register_to_id"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  required
                >
                  <option value="">Seleccionar caja</option>
                  <option
                    v-for="register in availableRegisters"
                    :key="register.id"
                    :value="register.id"
                  >
                    {{ register.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                @click="showTransactionModal = false"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="transactionForm.processing"
                class="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700 disabled:opacity-50"
              >
                <span v-if="transactionForm.processing">Procesando...</span>
                <span v-else>Crear Transacción</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal de Retiro de Efectivo -->
    <withdrawal-modal
      :show="showWithdrawalModal"
      :cash-register-id="cashRegister.id"
      @close="showWithdrawalModal = false"
      :is-admin="$page.props.auth?.user?.role === 'admin'"
    />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination.vue'
import WithdrawalModal from '@/Shared/WithdrawalModal.vue'

export default {
  name: 'CashRegistersShow',

  components: {
    Head,
    Link,
    Pagination,
    WithdrawalModal,
  },

  layout: Layout,

  props: {
    cashRegister: {
      type: Object,
      required: true
    },
    transactions: {
      type: Object,
      required: true
    },
    closures: {
      type: Array,
      default: () => []
    },
    availableRegisters: {
      type: Array,
      default: () => []
    },
    summary: {
      type: Object,
      default: () => null
    }
  },

  data() {
    return {
      activeTab: 'transactions',
      showTransactionModal: false,
      showWithdrawalModal: false,
      transactionForm: this.$inertia.form({
        type: 'withdrawal',
        amount: '',
        description: '',
        cash_register_to_id: '',
      }),
    }
  },

  methods: {
    back() {
      this.$inertia.visit('/cash-registers')
    },

    getStatusClass(status) {
      return {
        'open': 'bg-green-100 text-green-800',
        'closed': 'bg-red-100 text-red-800',
      }[status] || 'bg-gray-100 text-gray-800'
    },

    getStatusText(status) {
      return {
        'open': 'Abierta',
        'closed': 'Cerrada',
      }[status] || 'Desconocido'
    },

    getTransactionTypeClass(type) {
      const classes = {
        'opening': 'bg-blue-100 text-blue-800',
        'sale': 'bg-green-100 text-green-800',
        'payment': 'bg-green-100 text-green-800',
        'withdrawal': 'bg-red-100 text-red-800',
        'deposit': 'bg-blue-100 text-blue-800',
        'transfer_in': 'bg-purple-100 text-purple-800',
        'transfer_out': 'bg-purple-100 text-purple-800',
        'closure': 'bg-gray-100 text-gray-800',
      }
      return classes[type] || 'bg-gray-100 text-gray-800'
    },

    getTransactionTypeText(type) {
      const texts = {
        'opening': 'Apertura',
        'sale': 'Venta',
        'payment': 'Pago',
        'withdrawal': 'Retiro',
        'deposit': 'Depósito',
        'transfer_in': 'Transferencia Entrante',
        'transfer_out': 'Transferencia Saliente',
        'closure': 'Cierre',
      }
      return texts[type] || type
    },

    formatCurrency(value, currency = 'USD') {
      const currencies = {
        'USD': 'en-US',
        'VES': 'es-VE',
        'COP': 'es-CO'
      }

      const symbols = {
        'USD': 'USD',
        'VES': 'VES',
        'COP': 'COP'
      }

      return new Intl.NumberFormat(currencies[currency] || 'en-US', {
        style: 'currency',
        currency: symbols[currency] || 'USD'
      }).format(value || 0)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-VE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    openCashRegister() {
      if (confirm('¿Estás seguro de que quieres abrir esta caja?')) {
        this.$inertia.get(`/cash-registers/${this.cashRegister.id}/open`)
      }
    },

    closeCashRegister() {
      if (confirm('¿Estás seguro de que quieres cerrar esta caja? Se generará un reporte de cierre.')) {
        // Mostrar modal/formulario inline para declarar saldos reales
        const usdAmount = prompt('Saldo real en USD:', this.cashRegister.current_balance.toString());
        if (usdAmount === null) return; // Cancelado

        const bsAmount = prompt('Saldo real en Bolívares (BS):', this.cashRegister.current_balance_bs.toString());
        if (bsAmount === null) return; // Cancelado

        const copAmount = prompt('Saldo real en Pesos Colombianos (COP):', this.cashRegister.current_balance_cop.toString());
        if (copAmount === null) return; // Cancelado

        const notes = prompt('Notas (opcional):', '');

        // Validar que sean números
        const usd = parseFloat(usdAmount) || 0;
        const bs = parseFloat(bsAmount) || 0;
        const cop = parseFloat(copAmount) || 0;

        if (confirm(`¿Confirmar cierre de caja con:\n- USD: ${usd}\n- BS: ${bs}\n- COP: ${cop}\n- Notas: ${notes || 'Sin notas'}`)) {
          this.$inertia.post(`/cash-registers/${this.cashRegister.id}/close`, {
            closing_balance_usd: usd,
            closing_balance_bs: bs,
            closing_balance_cop: cop,
            notes: notes || null,
          }, {
            onSuccess: () => {
              this.$inertia.visit(`/cash-registers/${this.cashRegister.id}`)
            }
          })
        }
      }
    },

    createTransaction() {
      this.transactionForm.post('/cash-transactions', {
        onSuccess: () => {
          this.showTransactionModal = false
          this.transactionForm.reset()
        }
      })
    },
  }
}
</script>